import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { fetchNewCourses } from '../../../actions/course.actions';
import useAction from '../../../utils/useAction';
import { getNewCourses, getNewCoursesStatus } from '../../../reducers/dashboard.selectors';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import useRouter from '../../../utils/useRouter';
import { dashboardSection, routes } from '../../../constants';
import { CourseMindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';
import { getCourseMuiCardProps } from '../../cards/MuiCard/CourseMuiCard/utils';
import { isSchoolSubdomain } from '../../../utils/subdomain';
import { MIN_ITEMS, MAX_ITEMS } from '../../../routes/DashboardPage/hooks/useDashboardRowFetch';
import DashboardRowShell from '../DashboardRowShell/DashboardRowShell';

/**
 * Newest catalog courses on the new dashboard (PRO-40). Reuses the legacy
 * `newCourses` slice + fetch. Never shown on the schulen subdomain (courses are
 * not part of that offering); hidden on a fetch error or when fewer than
 * MIN_ITEMS courses resolve.
 */
const CoursesRow: React.FC = () => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const fetchNewCoursesAction = useAction(fetchNewCourses);

  const courses = useSelector(getNewCourses) ?? [];
  const status = useSelector(getNewCoursesStatus) ?? AsyncActionStatus.INITIAL;
  const isSchulenUser = isSchoolSubdomain(window.location.hostname);

  useEffect(() => {
    if (isSchulenUser) return;
    fetchNewCoursesAction();
  }, []);

  const isLoading =
    status === AsyncActionStatus.INITIAL || status === AsyncActionStatus.LOADING;
  const hidden =
    isSchulenUser ||
    status === AsyncActionStatus.ERROR ||
    (!isLoading && courses.length < MIN_ITEMS);

  return (
    <DashboardRowShell
      title={t('New in the course catalog')}
      onShowAll={() => router.push(`/${routes.courseCatalog.root}`)}
      loading={isLoading}
      hidden={hidden}
    >
      {courses.slice(0, MAX_ITEMS).map((course: CourseMindItemSearchResult) => (
        <DashboardMuiCard
          key={course.id}
          {...getCourseMuiCardProps(course)}
          dashboardSection={dashboardSection.courses}
        />
      ))}
    </DashboardRowShell>
  );
};

export default CoursesRow;
